/**
 * Lee un archivo adjunto y lo prepara para el chat según su tipo. La regla:
 *  - Texto y PDF → se extrae el TEXTO y se le pasa al modelo como contexto. Así
 *    funciona con cualquier modelo, aunque no sea multimodal.
 *  - Imágenes → se mandan como imagen (data URL); sólo sirven si el agente tiene
 *    marcado que su modelo acepta visión.
 *  - Lo demás (audio, vídeo…) → no se puede meter en un chat de texto; se avisa.
 *
 * pdf.js se carga de forma diferida: sólo pesa cuando de verdad adjuntas un PDF.
 */

export type AttachmentKind = 'text' | 'image' | 'unsupported'

export interface Attachment {
  name: string
  kind: AttachmentKind
  /** Texto extraído (kind 'text'). */
  text?: string
  /** data URL de la imagen (kind 'image'). */
  dataUrl?: string
  /** Motivo, cuando kind es 'unsupported'. */
  reason?: string
}

/** Extensiones que se leen como texto plano aunque el navegador no les ponga MIME. */
const TEXT_EXTENSIONS = /\.(txt|md|markdown|csv|tsv|json|xml|html?|ya?ml|log|ini|conf|ts|tsx|js|jsx|py|java|c|cpp|cs|go|rb|php|sql|sh)$/i

function readAsText(file: File): Promise<string> {
  return file.text()
}

function readAsDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(String(reader.result))
    reader.onerror = () => reject(reader.error)
    reader.readAsDataURL(file)
  })
}

/** Saca el texto de un PDF, página a página, con pdf.js. */
async function extractPdfText(file: File): Promise<string> {
  // Import diferido: la librería no entra en el bundle inicial.
  const pdfjs = await import('pdfjs-dist')
  // El worker va como URL de asset (patrón recomendado con Vite).
  const workerUrl = (await import('pdfjs-dist/build/pdf.worker.min.mjs?url')).default
  pdfjs.GlobalWorkerOptions.workerSrc = workerUrl

  const buffer = await file.arrayBuffer()
  const pdf = await pdfjs.getDocument({ data: buffer }).promise
  const parts: string[] = []
  for (let page = 1; page <= pdf.numPages; page++) {
    const content = await (await pdf.getPage(page)).getTextContent()
    // Los items son TextItem (tienen `str`) o TextMarkedContent (no); sólo el texto.
    const line = content.items.map(item => ('str' in item ? item.str : '')).join(' ')
    parts.push(line)
  }
  return parts.join('\n\n').trim()
}

export async function readAttachment(file: File): Promise<Attachment> {
  const isText = file.type.startsWith('text/') || TEXT_EXTENSIONS.test(file.name)
  const isPdf = file.type === 'application/pdf' || /\.pdf$/i.test(file.name)
  const isImage = file.type.startsWith('image/')

  try {
    if (isImage) {
      return { name: file.name, kind: 'image', dataUrl: await readAsDataUrl(file) }
    }
    if (isPdf) {
      const text = await extractPdfText(file)
      if (!text) return { name: file.name, kind: 'unsupported', reason: 'El PDF no tiene texto (¿escaneado?).' }
      return { name: file.name, kind: 'text', text }
    }
    if (isText) {
      return { name: file.name, kind: 'text', text: await readAsText(file) }
    }
    return { name: file.name, kind: 'unsupported', reason: 'Tipo de archivo no admitido en el chat.' }
  } catch {
    return { name: file.name, kind: 'unsupported', reason: 'No se pudo leer el archivo.' }
  }
}

/** Junta los textos de los documentos como contexto, antes del mensaje del usuario. */
export function buildMessageWithDocuments(text: string, docs: Attachment[]): string {
  const withText = docs.filter(d => d.kind === 'text' && d.text)
  if (!withText.length) return text

  const blocks = withText.map(d => `--- Documento adjunto: ${d.name} ---\n${d.text}`).join('\n\n')
  return `${blocks}\n\n--- Mensaje ---\n${text}`
}
